import React, { useMemo, useState, useRef, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { fi as fiLocale, enUS } from "date-fns/locale";
import { format } from "date-fns";
import { useI18n } from "@/contexts/I18nContext";
import { useConfig } from "@/hooks/useConfig";
import { api } from "@/lib/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { PhoneIcon, WhatsAppIcon } from "@/components/Icons";
import { Edit } from "@/contexts/EditModeContext";

const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00"];

const formatDurationLabel = (minutes, lang = "fi") => {
  const m = parseInt(minutes, 10);
  if (!Number.isFinite(m) || m <= 0) return `${minutes}`;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (r === 0) return lang === "fi" ? `${h} h` : `${h} h`;
  return lang === "fi" ? `${h} h ${r}` : `${h} h ${r}`;
};

/* Real road distance via backend API. Returns null while loading
   or unknown, a number (km) once geocoded. */
const useRealDistance = (from, to) => {
  const [km, setKm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const a = from.trim();
    const b = to.trim();
    if (a.length < 4 || b.length < 4) {
      setKm(null);
      setError("");
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const data = await api.distance(a, b);
        if (!cancelled) setKm(data.km);
      } catch (e) {
        if (!cancelled) {
          setKm(null);
          setError(
            e?.response?.data?.detail ||
              "Adresses non reconnues. Précise la rue + la ville.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [from, to]);

  return { km, loading, error };
};

const Field = ({ icon, children, onClick, clickable = false }) => (
  <div
    className="flex items-center gap-3 px-4 py-3 rounded-2xl border bg-white"
    style={{ borderColor: "#DCE3ED", cursor: clickable ? "pointer" : "default" }}
    onClick={onClick}
  >
    <span style={{ color: "#7B8AA1", flexShrink: 0 }}>{icon}</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

const PinIcon = ({ color = "currentColor" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

export default function BookingWidget({ initialService = "driverHelp" }) {
  const { t, lang } = useI18n();
  const config = useConfig();
  const SERVICES_ARR = [
    { id: "noLabour", ...config.services.noLabour },
    { id: "driverHelp", ...config.services.driverHelp },
    { id: "twoMen", ...config.services.twoMen },
  ];

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [date, setDate] = useState(new Date());
  const [showCal, setShowCal] = useState(false);
  const [time, setTime] = useState("");
  /* Dynamic duration list from config.durations (Object<minutes,price>) */
  const DURATIONS_ARR = useMemo(() => {
    const entries = Object.entries(config.durations || {})
      .map(([id, price]) => ({
        id: String(id),
        minutes: parseInt(id, 10),
        price: Number(price) || 0,
      }))
      .filter((d) => Number.isFinite(d.minutes) && d.minutes > 0)
      .sort((a, b) => a.minutes - b.minutes);
    return entries.map((d) => ({
      ...d,
      label: formatDurationLabel(d.minutes, lang),
    }));
  }, [config.durations, lang]);

  const [duration, setDuration] = useState(() => {
    const first = Object.keys(config.durations || {})[0];
    return first ? String(first) : "60";
  });

  /* Snap duration to a valid id if the list changes */
  useEffect(() => {
    if (DURATIONS_ARR.length === 0) return;
    if (!DURATIONS_ARR.some((d) => d.id === duration)) {
      setDuration(DURATIONS_ARR[0].id);
    }
  }, [DURATIONS_ARR, duration]);
  const [service, setService] = useState(initialService);
  const [error, setError] = useState("");
  const [showResult, setShowResult] = useState(false);
  const calRef = useRef(null);

  /* Real road distance (debounced) */
  const { km: realKm, loading: kmLoading, error: kmError } = useRealDistance(from, to);

  /* Close calendar on outside click */
  useEffect(() => {
    if (!showCal) return;
    const handler = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) {
        setShowCal(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCal]);

  const distance = realKm ?? 0;
  const breakdown = useMemo(() => {
    const svc = SERVICES_ARR.find((s) => s.id === service) || SERVICES_ARR[0];
    const incl = svc.includedKm || 0;
    const billableKm = Math.max(0, distance - incl);
    const km = billableKm * svc.perKm;
    const durFlat =
      (svc.durations && svc.durations[duration] !== undefined
        ? Number(svc.durations[duration])
        : null) ??
      Number(config.durations[duration]) ??
      0;
    const durMeta =
      DURATIONS_ARR.find((d) => d.id === duration) || DURATIONS_ARR[0] || { label: "" };
    const total = svc.base + km + durFlat;
    return {
      base: svc.base,
      km: Math.round(km),
      kmCount: distance,
      billableKm: Math.round(billableKm),
      includedKm: incl,
      service: svc.id,
      time: durFlat,
      timeLabel: durMeta.label,
      total: Math.round(total),
    };
  }, [service, distance, duration, SERVICES_ARR, config.durations, DURATIONS_ARR]);

  /* Blocked dates from config */
  const blocked = useMemo(
    () => (config.blockedDates || []).map((d) => new Date(d + "T00:00:00")),
    [config.blockedDates],
  );

  const submit = (e) => {
    e?.preventDefault?.();
    if (!from.trim() || !to.trim()) {
      setError(t.booking.missing);
      return;
    }
    if (realKm === null) {
      setError(kmError || "Patientez quelques secondes que la distance soit calculée.");
      return;
    }
    setError("");
    setShowResult(true);
  };

  const reset = () => setShowResult(false);

  const locale = lang === "fi" ? fiLocale : enUS;
  const dateLabel = format(date, "EEE d MMM yyyy", { locale });
  const isoDate = format(date, "yyyy-MM-dd");

  const waMessage = encodeURIComponent(
    (lang === "fi"
      ? `Hei Anypaku! Haluan varata muuton:\n• Lähtö: ${from}\n• Kohde: ${to}\n• Päivä: ${dateLabel} ${time || ""}\n• Kesto: ${breakdown.timeLabel}\n• Palvelu: ${t.services[service].t}\n• Arvio: ${breakdown.total} €\nVoinko vahvistaa?`
      : `Hi Anypaku! I'd like to book a move:\n• From: ${from}\n• To: ${to}\n• Date: ${dateLabel} ${time || ""}\n• Duration: ${breakdown.timeLabel}\n• Service: ${t.services[service].t}\n• Estimate: €${breakdown.total}\nCan I confirm?`).slice(0, 1500),
  );
  const waBookHref = `${config.whatsappLink}?text=${waMessage}`;

  return (
    <form
      onSubmit={submit}
      className="rounded-3xl p-6 md:p-8 shadow-2xl"
      style={{ background: "#FFFFFF", border: "1px solid #DCE3ED" }}
      data-testid="booking-widget"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-2xl md:text-3xl font-extrabold" style={{ color: "var(--navy)", fontFamily: "var(--font-display)", letterSpacing: "0.5px", textTransform: "uppercase" }}>
            <Edit path="booking.heading">{t.booking.heading}</Edit>
          </h3>
          <p className="text-sm mt-1" style={{ color: "var(--mute)" }}>
            <Edit path="booking.sub">{t.booking.sub}</Edit>
          </p>
        </div>
        <span
          className="hidden sm:inline-flex"
          style={{
            background: "var(--orange-soft)",
            color: "var(--orange)",
            padding: "6px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.1em",
          }}
        >
          {t.hero.from} {config.services.noLabour.base} €/h
        </span>
      </div>

      {!showResult ? (
        <div className="space-y-3">
          <AddressAutocomplete
            value={from}
            onChange={setFrom}
            placeholder={t.booking.fromPh}
            icon={<PinIcon color="var(--navy)" />}
            testid="input-from"
            dotColor="navy"
          />
          <AddressAutocomplete
            value={to}
            onChange={setTo}
            placeholder={t.booking.toPh}
            icon={<PinIcon color="var(--orange)" />}
            testid="input-to"
            dotColor="orange"
          />

          {/* Real km indicator */}
          {(from.trim().length >= 4 && to.trim().length >= 4) && (
            <div
              className="px-4 py-2 rounded-xl text-xs flex items-center justify-between"
              style={{
                background: kmError ? "#FFE9EA" : "var(--cream-2)",
                color: kmError ? "#B3263F" : "var(--mute)",
              }}
              data-testid="distance-indicator"
            >
              {kmLoading ? (
                <span>⏳ Calcul de la distance routière…</span>
              ) : kmError ? (
                <span>⚠️ {kmError}</span>
              ) : realKm !== null ? (
                <>
                  <span>📍 Distance routière estimée</span>
                  <span style={{ color: "var(--navy)", fontWeight: 700 }}>{realKm} km</span>
                </>
              ) : null}
            </div>
          )}

          {/* Date — real calendar */}
          <div className="relative" ref={calRef}>
            <Field
              icon={<CalendarIcon />}
              clickable
              onClick={() => setShowCal((v) => !v)}
            >
              <div
                className="flex items-center justify-between"
                data-testid="date-trigger"
              >
                <span style={{ color: "var(--navy)" }}>{dateLabel}</span>
                <span style={{ color: "var(--mute)" }}>
                  <ChevronDown />
                </span>
              </div>
            </Field>
            {showCal && (
              <div
                className="absolute z-30 mt-2 rounded-2xl shadow-2xl p-3 left-0 right-0 sm:right-auto"
                style={{ background: "#FFFFFF", border: "1px solid #DCE3ED" }}
                data-testid="calendar-popover"
              >
                <DayPicker
                  mode="single"
                  selected={date}
                  onSelect={(d) => {
                    if (d) {
                      setDate(d);
                      setShowCal(false);
                    }
                  }}
                  locale={locale}
                  disabled={[{ before: new Date() }, ...blocked]}
                  weekStartsOn={1}
                  showOutsideDays
                  modifiersClassNames={{
                    selected: "anypaku-day-selected",
                    today: "anypaku-day-today",
                  }}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field icon={<ClockIcon />}>
              <select
                data-testid="select-time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full bg-transparent outline-none text-[15px]"
                style={{ color: "var(--navy)" }}
              >
                <option value="">{t.booking.asap}</option>
                {TIME_SLOTS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field icon={<ClockIcon />}>
              <select
                data-testid="select-duration"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full bg-transparent outline-none text-[15px]"
                style={{ color: "var(--navy)" }}
              >
                {DURATIONS_ARR.map((d) => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
            </Field>
          </div>

          <div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--mute)",
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              {t.booking.service}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {SERVICES_ARR.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setService(s.id)}
                  data-testid={`service-pill-${s.id}`}
                  className="px-4 py-3.5 rounded-xl text-left transition-all flex items-center justify-between sm:block"
                  style={{
                    border: service === s.id ? "2px solid var(--orange)" : "1px solid #DCE3ED",
                    background: service === s.id ? "var(--orange-soft)" : "#FFFFFF",
                  }}
                >
                  <div>
                    <div
                      className="text-[13px] font-bold uppercase"
                      style={{ color: "var(--navy)", letterSpacing: "0.05em" }}
                    >
                      {t.services[s.id].t}
                    </div>
                    <div className="text-[12px] mt-1" style={{ color: "var(--mute)" }}>
                      {t.hero.from} {s.base} €/h
                    </div>
                  </div>
                  {service === s.id && (
                    <span
                      className="sm:hidden"
                      style={{ color: "var(--orange)", fontSize: 22, fontWeight: 800 }}
                    >
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div
              className="px-4 py-3 rounded-xl text-sm"
              style={{ background: "#FFE9EA", color: "#B3263F", border: "1px solid #F5C2C8" }}
              data-testid="error-message"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full mt-2 px-6 py-4 rounded-2xl text-base font-bold transition-transform hover:-translate-y-0.5"
            style={{ background: "var(--navy)", color: "#FFFFFF", fontFamily: "var(--font-sans)" }}
            data-testid="see-price-btn"
          >
            {t.booking.seePrice} →
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="booking-result">
          <div className="rounded-2xl p-5" style={{ background: "var(--navy)", color: "#FFFFFF" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", opacity: 0.7 }}>
              {t.booking.result}
            </div>
            <div className="flex items-baseline gap-2 mt-1" style={{ fontFamily: "var(--font-display)", fontWeight: 900 }}>
              <span style={{ fontSize: 56, lineHeight: 1 }}>{breakdown.total}</span>
              <span style={{ fontSize: 28, color: "var(--orange)" }}>€</span>
            </div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6, fontFamily: "var(--font-sans)" }}>
              {dateLabel} {time && `· ${time}`}
            </div>
            <div style={{ fontSize: 12, opacity: 0.65, marginTop: 2 }}>{t.booking.resultSub}</div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between" style={{ color: "var(--midnight)" }}>
              <span>
                {t.booking.pieces.base}
                {breakdown.includedKm > 0 && (
                  <span style={{ color: "var(--mute)", fontSize: 12 }}>
                    {" "}
                    ({breakdown.includedKm} km {lang === "fi" ? "sis." : "incl."})
                  </span>
                )}
              </span>
              <span>{breakdown.base} €</span>
            </div>
            <div className="flex justify-between" style={{ color: "var(--midnight)" }}>
              <span>
                {t.booking.pieces.km}{" "}
                <span style={{ color: "var(--mute)", fontSize: 12 }}>
                  ({breakdown.kmCount} km
                  {breakdown.includedKm > 0 &&
                    ` · ${breakdown.billableKm} km ${lang === "fi" ? "lisämaksu" : "billable"}`}
                  )
                </span>
              </span>
              <span>{breakdown.km} €</span>
            </div>
            <div className="flex justify-between" style={{ color: "var(--midnight)" }}>
              <span>{t.booking.pieces.time} ({breakdown.timeLabel})</span>
              <span>{breakdown.time} €</span>
            </div>
            <div className="flex justify-between pt-2 border-t font-bold" style={{ color: "var(--navy)", borderColor: "#DCE3ED" }}>
              <span>Total</span>
              <span>{breakdown.total} €</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <a
              href={`tel:${config.phoneRaw}`}
              data-testid="confirm-call"
              className="px-5 py-3 rounded-2xl text-center font-bold transition-transform hover:-translate-y-0.5"
              style={{ background: "var(--navy)", color: "#FFFFFF" }}
            >
              <span className="inline-flex items-center justify-center gap-2">
                <PhoneIcon size={16} /> {t.booking.bookCall}
              </span>
              <div className="text-xs font-normal opacity-80 mt-0.5">{config.phoneDisplay}</div>
            </a>
            <a
              href={waBookHref}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="confirm-wa"
              className="px-5 py-3 rounded-2xl text-center font-bold transition-transform hover:-translate-y-0.5"
              style={{ background: "var(--orange)", color: "#FFFFFF" }}
            >
              <span className="inline-flex items-center justify-center gap-2">
                <WhatsAppIcon size={18} /> {t.booking.bookWA}
              </span>
              <div className="text-xs font-normal opacity-90 mt-0.5">→ {config.phoneDisplay}</div>
            </a>
          </div>

          <button
            type="button"
            onClick={reset}
            className="w-full text-sm py-2 mt-2"
            style={{ color: "var(--mute)" }}
            data-testid="new-quote"
          >
            ← {t.booking.newQuote}
          </button>
        </div>
      )}
    </form>
  );
}

/* Re-export so other components keep working without imports change.
   Reads live from config at call time. */
export const PHONE_RAW = "+358401683383";
export const PHONE_DISPLAY = "+358 40 168 3383";
export const WA_LINK = "https://wa.me/358401683383";
